import dayjs, { type Dayjs } from 'dayjs';
import {
    isTimedSlotInOutage,
    type VenueAvailabilityOutageInput,
} from '@/entities/venue/lib/venue-availability.utils';

type OpeningInterval = { start: string; end: string };
type OpeningDay = { date: string; availableIntervals: OpeningInterval[] };

/** Timed-order slots for a calendar date, excluding past / lead-time / outage windows. */
export const getTimedOrderSlots = ({
    openingDays,
    date,
    now,
    intervalMinutes,
    minimumMinutesFromCurrentTime,
    outages,
}: {
    openingDays: OpeningDay[];
    date: string;
    now: Dayjs;
    intervalMinutes: number;
    minimumMinutesFromCurrentTime: number;
    outages: VenueAvailabilityOutageInput[];
}): Array<{ start: Dayjs; end: Dayjs }> => {
    const selectedOpening = openingDays.find((oh) => oh.date.startsWith(date));
    if (!selectedOpening) {
        return [];
    }

    const nowPlusOffset = now.add(minimumMinutesFromCurrentTime, 'minutes');
    const slots: Array<{ start: Dayjs; end: Dayjs }> = [];

    selectedOpening.availableIntervals.forEach((interval) => {
        const rawStart = dayjs(interval.start.replace(/[+-]\d{2}:\d{2}$/, ''));
        const rawEnd = dayjs(interval.end.replace(/[+-]\d{2}:\d{2}$/, ''));
        const start = rawStart.minute() === 1 ? rawStart.subtract(1, 'minute') : rawStart;
        const end = rawEnd.minute() === 59 ? rawEnd.add(1, 'minute') : rawEnd;

        const minutesSinceMidnight = start.hour() * 60 + start.minute();
        const alignedMinutes = Math.ceil(minutesSinceMidnight / intervalMinutes) * intervalMinutes;
        let current = start.startOf('day').add(alignedMinutes, 'minutes');
        while (!current.add(intervalMinutes, 'minutes').isAfter(end)) {
            const slotStart = current;
            const slotEnd = current.add(intervalMinutes, 'minutes');
            if (
                slotStart.isAfter(nowPlusOffset) &&
                !isTimedSlotInOutage(slotStart, slotEnd, outages, minimumMinutesFromCurrentTime)
            ) {
                slots.push({ start: slotStart, end: slotEnd });
            }
            current = slotEnd;
        }
    });

    return slots;
};
